<script setup lang="ts">
import { computed } from 'vue'
import Box from './Box.vue'

export type SkeletonShape = 'rectangle' | 'circle'
export type SkeletonAnimation = 'pulse' | 'wave' | 'none'

interface Props {
  /** Shape — `rectangle` (default) or `circle`. */
  shape?: SkeletonShape
  /** Shorthand: both width + height (for circles/squares). */
  size?: string
  /** Width (defaults to 100%). */
  width?: string
  /** Height (defaults to 1rem for rectangle, equal to width for circle). */
  height?: string
  /** Border radius override. Defaults: rectangle = 6px, circle = 50%. */
  borderRadius?: string
  /** Animation type. Default `pulse`. */
  animation?: SkeletonAnimation
}

const props = withDefaults(defineProps<Props>(), {
  shape: 'rectangle',
  animation: 'pulse',
})

const widthValue = computed(() => props.size ?? props.width ?? '100%')
const heightValue = computed(() => props.size ?? props.height ?? '1rem')
const radius = computed(
  () => props.borderRadius ?? (props.shape === 'circle' ? '50%' : '6px'),
)
</script>

<template>
  <Box
    class="skeleton"
    :class="`skeleton--anim-${animation}`"
    :style="{ width: widthValue, height: heightValue, borderRadius: radius }"
  />
</template>

<style scoped>
.skeleton {
  background: var(--p-surface-200);
  display: block;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

/* Pulse — opacity oscillation, matches existing module skeletons */
.skeleton--anim-pulse {
  animation: skeleton-pulse 1.4s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

/* Wave — gradient shimmer across the surface */
.skeleton--anim-wave {
  background: linear-gradient(
    90deg,
    var(--p-surface-100) 25%,
    var(--p-surface-200) 50%,
    var(--p-surface-100) 75%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite linear;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
